/**
 * React hooks for bow / arrow profiles.
 *
 * Returns reactive state synced with localStorage. Multiple components
 * using the same hook see updates immediately via a shared event bus.
 */

import { useState, useEffect, useCallback } from 'react';
import type { BowProfile, ArrowProfile } from './types';
import {
  loadBowProfiles,
  loadArrowProfiles,
  saveBowProfiles,
  saveArrowProfiles,
  mergeBowProfiles,
  mergeArrowProfiles,
  upsertBowProfile as upsertBow,
  upsertArrowProfile as upsertArrow,
  deleteBowProfile as deleteBow,
  deleteArrowProfile as deleteArrow,
  hydrateProfilesFromParent,
} from './storage';
import { fetchIdentity } from '@/auth/identityBridge';
import {
  fetchRemoteBowProfiles,
  fetchRemoteArrowProfiles,
  pushRemoteBowProfiles,
  pushRemoteArrowProfiles,
} from '@/sync/cloudSync';

/* Lightweight event bus so multiple components stay in sync */
type ProfileEvent = 'bow-updated' | 'arrow-updated';
const listeners: Record<ProfileEvent, Set<() => void>> = {
  'bow-updated': new Set(),
  'arrow-updated': new Set(),
};
function emit(event: ProfileEvent) {
  listeners[event].forEach((fn) => fn());
}
function subscribe(event: ProfileEvent, fn: () => void): () => void {
  listeners[event].add(fn);
  return () => listeners[event].delete(fn);
}

/**
 * Restore profiles from the durable parent-storefront mirror on first mount
 * (incident 4c). Runs exactly once per page load regardless of how many
 * components use the profile hooks; when it restores anything it emits so
 * every mounted hook refreshes from the now-rehydrated local store.
 */
let hydrationStarted = false;
function ensureParentHydration(): void {
  if (hydrationStarted || typeof window === 'undefined') return;
  hydrationStarted = true;
  void hydrateProfilesFromParent()
    .then(({ bowsChanged, arrowsChanged }) => {
      if (bowsChanged) emit('bow-updated');
      if (arrowsChanged) emit('arrow-updated');
    })
    .catch(() => {
      /* fail-open: no parent bridge — local store stands as-is */
    });
}

/**
 * Cross-device profile sync (Layer 2) via the same identity-authed D1 worker
 * the Range Log uses. Layer 1 keeps profiles durable on ONE device; this makes
 * them survive a new phone / reinstall.
 *
 * On first mount (once per page load): if the visitor is a signed-in customer,
 * pull their cloud bow/arrow profiles and merge into local — reusing the
 * Layer-1 merges (union by profileId, newest updatedAt wins, single
 * primary/active enforced) so nothing is lost or clobbered — then push the
 * unioned set back so local-only profiles reach the cloud. Anonymous visitors
 * never touch the network. Best-effort throughout: any failure leaves the local
 * store exactly as it was.
 */
let cloudHydrationStarted = false;
let cloudSignedIn = false;
const CLOUD_PUSH_DEBOUNCE_MS = 1500;
const cloudPushTimers: Record<'bow' | 'arrow', number | undefined> = {
  bow: undefined,
  arrow: undefined,
};

function ensureCloudHydration(): void {
  if (cloudHydrationStarted || typeof window === 'undefined') return;
  cloudHydrationStarted = true;
  void (async () => {
    const identity = await fetchIdentity();
    if (!identity.signedIn) return; // anonymous — local-only, no network
    cloudSignedIn = true;

    const [remoteBows, remoteArrows] = await Promise.all([
      fetchRemoteBowProfiles(),
      fetchRemoteArrowProfiles(),
    ]);

    if (remoteBows && remoteBows.length > 0) {
      const merged = mergeBowProfiles(loadBowProfiles(), remoteBows);
      saveBowProfiles(merged);
      emit('bow-updated');
    }
    if (remoteArrows && remoteArrows.length > 0) {
      const merged = mergeArrowProfiles(loadArrowProfiles(), remoteArrows);
      saveArrowProfiles(merged);
      emit('arrow-updated');
    }

    // Push the (possibly unioned) local state up so profiles created on this
    // device — or before sign-in — land in the cloud.
    scheduleCloudPush('bow');
    scheduleCloudPush('arrow');
  })().catch(() => {
    /* fail-open: offline / no bridge — local store stands as-is */
  });
}

/** Debounced best-effort push of the current local profiles to the cloud. */
function scheduleCloudPush(which: 'bow' | 'arrow'): void {
  if (!cloudSignedIn || typeof window === 'undefined') return;
  const pending = cloudPushTimers[which];
  if (pending !== undefined) window.clearTimeout(pending);
  cloudPushTimers[which] = window.setTimeout(() => {
    cloudPushTimers[which] = undefined;
    if (which === 'bow') void pushRemoteBowProfiles(loadBowProfiles());
    else void pushRemoteArrowProfiles(loadArrowProfiles());
  }, CLOUD_PUSH_DEBOUNCE_MS);
}

export function useBowProfiles() {
  const [profiles, setProfiles] = useState<BowProfile[]>(() => loadBowProfiles());

  useEffect(() => {
    ensureParentHydration();
    ensureCloudHydration();
    return subscribe('bow-updated', () => setProfiles(loadBowProfiles()));
  }, []);

  const upsert = useCallback((profile: BowProfile) => {
    const updated = upsertBow(profile);
    setProfiles(updated);
    emit('bow-updated');
    scheduleCloudPush('bow');
    return updated;
  }, []);

  const remove = useCallback((profileId: string) => {
    const updated = deleteBow(profileId);
    setProfiles(updated);
    emit('bow-updated');
    scheduleCloudPush('bow');
    return updated;
  }, []);

  const setPrimary = useCallback(
    (profileId: string) => {
      const target = profiles.find((p) => p.profileId === profileId);
      if (!target) return profiles;
      const updated = upsertBow({ ...target, isPrimary: true, updatedAt: Date.now() });
      setProfiles(updated);
      emit('bow-updated');
      scheduleCloudPush('bow');
      return updated;
    },
    [profiles]
  );

  const primary = profiles.find((p) => p.isPrimary);

  return { profiles, primary, upsert, remove, setPrimary };
}

export function useArrowProfiles() {
  const [profiles, setProfiles] = useState<ArrowProfile[]>(() => loadArrowProfiles());

  useEffect(() => {
    ensureParentHydration();
    ensureCloudHydration();
    return subscribe('arrow-updated', () => setProfiles(loadArrowProfiles()));
  }, []);

  const upsert = useCallback((profile: ArrowProfile) => {
    const updated = upsertArrow(profile);
    setProfiles(updated);
    emit('arrow-updated');
    scheduleCloudPush('arrow');
    return updated;
  }, []);

  const remove = useCallback((profileId: string) => {
    const updated = deleteArrow(profileId);
    setProfiles(updated);
    emit('arrow-updated');
    scheduleCloudPush('arrow');
    return updated;
  }, []);

  const setActive = useCallback(
    (profileId: string) => {
      const target = profiles.find((p) => p.profileId === profileId);
      if (!target) return profiles;
      const updated = upsertArrow({ ...target, isActive: true, updatedAt: Date.now() });
      setProfiles(updated);
      emit('arrow-updated');
      scheduleCloudPush('arrow');
      return updated;
    },
    [profiles]
  );

  const active = profiles.find((p) => p.isActive);

  return { profiles, active, upsert, remove, setActive };
}
